"use client";

import { useEffect, useReducer, useRef, useState } from "react";
import { useNavigationSearchParams } from "../../lib/navigation";
import { useTranslate } from "../../lib/translation";
import { cn } from "../../lib/cn";
import { motion, useReducedMotion } from "framer-motion";
import Button from "@dt/Button";
import { FormFieldEditorial } from "../FormFieldEditorial";
import { ExpandableSection } from "../ExpandableSection";
import { useToast } from "../../lib/toast";
import FileUpload from "@dt/FileUpload";
import Combobox from "@dt/Combobox";
import MultiCombobox from "@dt/MultiCombobox";
import CheckboxGroup from "@dt/CheckboxGroup";
import PhoneInput from "@dt/PhoneInput";
import styles from "./ContactFormEditorial.module.css";
import {
  CONTACT_ACCEPTED_ATTACHMENT_TYPES,
  CONTACT_ATTACHMENT_MAX_BYTES,
  CONTACT_EMAIL_ATTACHMENT_LIMIT_BYTES,
  CONTACT_HONEYPOT_INPUT_ID,
  CONTACT_HONEYPOT_INPUT_NAME,
  reportContactHoneypot,
  validateContactEmail,
} from "../contactFormUtils";
import { suggestEmailCorrection } from "../../utils/emailSuggestion";
import {
  DONNY_PREFILL_CONTACT_EVENT,
  type DonnyContactPrefillDetail,
} from "../DonnyActionProvider/donnyContactPrefill";
import { ENGAGEMENTS, getEngagementByPackageId } from "../../data/engagements";

// === PRESERVED CONSTANTS ===
const MAX_ATTACHMENT_BYTES = CONTACT_ATTACHMENT_MAX_BYTES;
const EMAIL_ATTACHMENT_LIMIT_BYTES = CONTACT_EMAIL_ATTACHMENT_LIMIT_BYTES;
const ACCEPTED_ATTACHMENT_TYPES = CONTACT_ACCEPTED_ATTACHMENT_TYPES;

// === FORM STATE ===
const getInitialFormState = () => ({
  name: "",
  email: "",
  phone: "",
  message: "",
  budget: "",
  timeline: "",
  /** Package id from data/engagements; "" means no package (custom). */
  engagementPackage: "",
  projectType: [] as string[],
  followUpPreferences: [] as string[],
  hearAbout: "",
  inspiration: "",
  honeypot: "",
});

const getInitialErrorState = () => ({
  name: "",
  email: "",
  message: "",
});

type FormState = ReturnType<typeof getInitialFormState>;
type ErrorState = ReturnType<typeof getInitialErrorState>;
type ValidatedField = keyof ErrorState;

type FormAction =
  | {
      type: "UPDATE_FIELD";
      payload: {
        field: Exclude<keyof FormState, "projectType" | "followUpPreferences">;
        value: string;
      };
    }
  | { type: "UPDATE_PROJECT_TYPES"; payload: string[] }
  | { type: "UPDATE_FOLLOW_UP_PREFERENCES"; payload: string[] }
  | { type: "ADD_PROJECT_TYPE"; payload: string }
  | { type: "SET_PACKAGE"; payload: string }
  | { type: "RESET" };

const mergeProjectType = (current: string[], next: string): string[] => {
  if (!next || current.includes(next)) return current;
  return [...current, next];
};

const formReducer = (state: FormState, action: FormAction): FormState => {
  switch (action.type) {
    case "UPDATE_FIELD":
      return { ...state, [action.payload.field]: action.payload.value };
    case "UPDATE_PROJECT_TYPES":
      return { ...state, projectType: action.payload };
    case "UPDATE_FOLLOW_UP_PREFERENCES":
      return {
        ...state,
        followUpPreferences: action.payload,
      };
    case "ADD_PROJECT_TYPE":
      return {
        ...state,
        projectType: mergeProjectType(state.projectType, action.payload),
      };
    case "SET_PACKAGE": {
      // Swap the previous package's implied project types for the new one's;
      // anything the visitor picked themselves stays. A package also sets the
      // budget, so the budget bracket is cleared.
      const previous = getEngagementByPackageId(state.engagementPackage);
      const next = getEngagementByPackageId(action.payload);
      const kept = state.projectType.filter(
        (type) => !previous?.projectTypes.includes(type),
      );
      return {
        ...state,
        engagementPackage: next ? next.packageId : "",
        budget: next ? "" : state.budget,
        projectType: (next?.projectTypes ?? []).reduce(mergeProjectType, kept),
      };
    }
    case "RESET":
      return getInitialFormState();
    default:
      return state;
  }
};

// === VALIDATION ===
const validateEmail = validateContactEmail;

// === OPTIONS ===
const BUDGET_OPTIONS = [
  { value: "under-5k", labelKey: "contactBudgetUnder5k" },
  { value: "5k-15k", labelKey: "contactBudget5k15k" },
  { value: "15k-50k", labelKey: "contactBudget15k50k" },
  { value: "50k-plus", labelKey: "contactBudget50kPlus" },
  { value: "not-sure", labelKey: "contactBudgetNotSure" },
];

const TIMELINE_OPTIONS = [
  { value: "asap", labelKey: "contactTimelineAsap" },
  { value: "1-2-months", labelKey: "contactTimeline1to2" },
  { value: "3-6-months", labelKey: "contactTimeline3to6" },
  { value: "flexible", labelKey: "contactTimelineFlexible" },
];

const PROJECT_TYPE_OPTIONS = [
  { value: "brand-identity", labelKey: "contactProjectBrand" },
  { value: "digital-product", labelKey: "contactProjectDigital" },
  { value: "website", labelKey: "contactProjectWebsite" },
  { value: "creative-direction", labelKey: "contactProjectCreative" },
  { value: "ds-audit", labelKey: "contactProjectDsAudit" },
  { value: "component-library", labelKey: "contactProjectComponentLib" },
  { value: "tokens-theming", labelKey: "contactProjectTokens" },
  { value: "ai-designops", labelKey: "contactProjectDesignOps" },
  { value: "other", labelKey: "contactProjectOther" },
];

const formatProjectTypesForSubmit = (
  values: string[],
  t: (key: string) => string,
): string =>
  values
    .map((value) => {
      const option = PROJECT_TYPE_OPTIONS.find((entry) => entry.value === value);
      return option ? t(option.labelKey) : value;
    })
    .join(", ");

/** Maps homepage CTA `?service=` query values to project-type slugs. */
const SERVICE_PROJECT_TYPE_MAP: Record<string, string> = {
  "design-sprint": "component-library",
  "design-sprints": "component-library",
};

const HEAR_ABOUT_OPTIONS = [
  { value: "social-media", labelKey: "contactHearSocial" },
  { value: "search-engine", labelKey: "contactHearSearch" },
  { value: "word-of-mouth", labelKey: "contactHearWord" },
  { value: "event", labelKey: "contactHearEvent" },
  { value: "existing-client", labelKey: "contactHearExisting" },
  { value: "other", labelKey: "contactHearOther" },
];

const FOLLOW_UP_OPTIONS = [
  { value: "portfolio-materials", labelKey: "contactRequestPortfolioMaterials" },
  { value: "call-prep", labelKey: "contactFollowUpCallPrep" },
];

const formatOptionsForSubmit = (
  values: string[],
  options: { value: string; labelKey: string }[],
  t: (key: string) => string,
): string =>
  values
    .map((value) => {
      const option = options.find((entry) => entry.value === value);
      return option ? t(option.labelKey) : value;
    })
    .join(", ");

export interface ContactFormEditorialProps {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
  className?: string;
}

/**
 * ContactFormEditorial component.
 */
export function ContactFormEditorial({
  onSuccess,
  onError,
  className,
}: ContactFormEditorialProps) {
  const t = useTranslate();
  const { showToast } = useToast();
  const searchParams = useNavigationSearchParams();
  // Honour reduced-motion: when set, skip the form's entrance animation entirely
  // so the rendered tree sits at its final visible state from frame 1. Without
  // this, axe samples the form mid-fade (opacity ~0.7) and reports the
  // ExpandableSection trigger/privacy paragraph as a color-contrast violation
  // because their muted-foreground color blends to ~3.5:1 against the page.
  const prefersReducedMotion = useReducedMotion();

  useEffect(() => {
    const engagement = getEngagementByPackageId(
      searchParams?.get("package")?.trim().toLowerCase(),
    );
    if (!engagement) return;

    dispatchForm({ type: "SET_PACKAGE", payload: engagement.packageId });
    setTier2Expanded(true);
  }, [searchParams]);

  useEffect(() => {
    const service = searchParams?.get("service")?.trim().toLowerCase();
    if (!service) return;

    const projectType = SERVICE_PROJECT_TYPE_MAP[service];
    if (!projectType) return;

    dispatchForm({
      type: "ADD_PROJECT_TYPE",
      payload: projectType,
    });
    setTier2Expanded(true);
  }, [searchParams]);

  useEffect(() => {
    const onDonnyPrefill = (event: Event) => {
      const detail = (event as CustomEvent<DonnyContactPrefillDetail>).detail;
      if (!detail) return;

      if (detail.projectType) {
        dispatchForm({
          type: "ADD_PROJECT_TYPE",
          payload: detail.projectType,
        });
        setTier2Expanded(true);
      }

      if (detail.message) {
        dispatchForm({
          type: "UPDATE_FIELD",
          payload: { field: "message", value: detail.message },
        });
      }
    };

    window.addEventListener(DONNY_PREFILL_CONTACT_EVENT, onDonnyPrefill);
    return () =>
      window.removeEventListener(DONNY_PREFILL_CONTACT_EVENT, onDonnyPrefill);
  }, []);

  // Form state
  const [formData, dispatchForm] = useReducer(
    formReducer,
    undefined,
    getInitialFormState,
  );
  const [formErrors, setFormErrors] =
    useState<ErrorState>(getInitialErrorState);
  const [emailSuggestion, setEmailSuggestion] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const nameRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const messageRef = useRef<HTMLTextAreaElement>(null);

  // Attachment state
  const [attachmentFile, setAttachmentFile] = useState<File | null>(null);
  const [attachmentDataUrl, setAttachmentDataUrl] = useState<string | null>(
    null,
  );
  const [attachmentError, setAttachmentError] = useState("");

  // Expansion state
  const [tier2Expanded, setTier2Expanded] = useState(false);
  const [tier3Expanded, setTier3Expanded] = useState(false);

  // Derived values
  const selectedEngagement = getEngagementByPackageId(formData.engagementPackage);
  const packageSummary = (engagement: (typeof ENGAGEMENTS)[number]) =>
    t("contactPackageOption", {
      name: t(engagement.nameKey),
      price: t(engagement.priceKey),
      duration: t(engagement.durationKey),
    });
  const attachmentSizeValue = (MAX_ATTACHMENT_BYTES / (1024 * 1024)).toFixed(0);
  const emailAttachmentLimitValue = (
    EMAIL_ATTACHMENT_LIMIT_BYTES /
    (1024 * 1024)
  ).toFixed(1);
  const isAttachmentTooLargeForEmail =
    !!attachmentFile && attachmentFile.size > EMAIL_ATTACHMENT_LIMIT_BYTES;
  const emailAttachmentLimitLabel = t("contactAttachmentEmailLimitLabel", {
    size: emailAttachmentLimitValue,
  });
  const attachmentEmailNotice = isAttachmentTooLargeForEmail
    ? t("contactAttachmentEmailLimitReached", {
        size: emailAttachmentLimitLabel,
      })
    : "";

  const getFieldError = (field: ValidatedField, value: string): string => {
    if (!value.trim()) {
      if (field === "name") return t("contactValidationFullNameRequired");
      if (field === "email") return t("contactValidationEmailRequired");
      return t("contactValidationMessageRequired");
    }

    if (field === "email" && !validateEmail(value)) {
      return t("contactValidationEmailInvalid");
    }

    return "";
  };

  const getEmailSuggestion = (value: string): string => {
    if (!validateEmail(value)) return "";
    const suggestion = suggestEmailCorrection(value);
    return suggestion
      ? t("contactValidationEmailSuggestion", { suggestion })
      : "";
  };

  const validateField = (field: ValidatedField, value: string) => {
    setFormErrors((current) => ({
      ...current,
      [field]: getFieldError(field, value),
    }));
    if (field === "email") setEmailSuggestion(getEmailSuggestion(value));
  };

  // === HANDLERS ===
  const updateField =
    (field: Exclude<keyof FormState, "projectType" | "followUpPreferences">) =>
    (
      e: React.ChangeEvent<
        HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
      >,
    ) => {
      dispatchForm({
        type: "UPDATE_FIELD",
        payload: { field, value: e.target.value },
      });

      if (
        (field === "name" || field === "email" || field === "message") &&
        (formErrors[field] || (field === "email" && emailSuggestion))
      ) {
        validateField(field, e.target.value);
      }
    };

  const updateSelectField =
    (field: "budget" | "timeline" | "hearAbout") => (value: string) => {
      dispatchForm({
        type: "UPDATE_FIELD",
        payload: { field, value },
      });
    };

  const handlePhoneChange = (value: string | undefined) => {
    dispatchForm({
      type: "UPDATE_FIELD",
      payload: { field: "phone", value: value ?? "" },
    });
  };

  const handleAttachmentChange = (file: File | null) => {
    setAttachmentError("");
    setAttachmentFile(file);
    setAttachmentDataUrl(null);
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === "string") {
        setAttachmentDataUrl(reader.result);
      } else {
        setAttachmentError(t("contactAttachmentReadError"));
        setAttachmentFile(null);
      }
    };
    reader.onerror = () => {
      setAttachmentError(t("contactAttachmentReadError"));
      setAttachmentFile(null);
    };
    reader.readAsDataURL(file);
  };

  const resetFormState = () => {
    dispatchForm({ type: "RESET" });
    setAttachmentFile(null);
    setAttachmentDataUrl(null);
    setAttachmentError("");
    setFormErrors(getInitialErrorState());
    setEmailSuggestion("");
    setTier2Expanded(false);
    setTier3Expanded(false);
  };

  // === SPAM PROTECTION ===
  const logHoneypotHit = () => reportContactHoneypot(formData.honeypot);

  // === VALIDATION ===
  const validateForm = (): ErrorState => {
    const errors: ErrorState = {
      name: getFieldError("name", formData.name),
      email: getFieldError("email", formData.email),
      message: getFieldError("message", formData.message),
    };

    setFormErrors(errors);
    setEmailSuggestion(getEmailSuggestion(formData.email));
    return errors;
  };

  const focusFirstInvalidField = (errors: ErrorState) => {
    requestAnimationFrame(() => {
      if (errors.name) nameRef.current?.focus();
      else if (errors.email) emailRef.current?.focus();
      else if (errors.message) messageRef.current?.focus();
    });
  };

  // === SUBMIT ===
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;

    // Honeypot check
    if (formData.honeypot.trim()) {
      logHoneypotHit();
      return;
    }

    const errors = validateForm();
    if (errors.name || errors.email || errors.message || attachmentError) {
      focusFirstInvalidField(errors);
      return;
    }

    setIsSubmitting(true);

    const now = new Date();
    const time = now.toLocaleString();

    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          interest: formatProjectTypesForSubmit(formData.projectType, t),
          message: formData.message,
          hearAbout: formData.hearAbout,
          budget: selectedEngagement ? "" : formData.budget,
          timeline: formData.timeline,
          engagementPackage: selectedEngagement
            ? packageSummary(selectedEngagement)
            : "",
          inspiration: formData.inspiration,
          followUpPreferences: formatOptionsForSubmit(
            formData.followUpPreferences,
            FOLLOW_UP_OPTIONS,
            t,
          ),
          requestPortfolioMaterials:
            formData.followUpPreferences.includes("portfolio-materials"),
          attachmentName: attachmentFile?.name ?? null,
          attachmentType: attachmentFile?.type ?? null,
          attachmentSize: attachmentFile?.size ?? null,
          attachmentData:
            attachmentDataUrl && !isAttachmentTooLargeForEmail
              ? attachmentDataUrl
              : null,
          attachmentNotice: attachmentEmailNotice,
          time,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to submit form");
      }

      showToast(t("contactSuccessMessage"), {
        tone: "success",
        duration: 5000,
      });
      resetFormState();
      onSuccess?.();
    } catch (err) {
      console.error("Failed to submit contact form", err);
      showToast(t("contactErrorMessage"), {
        tone: "error",
        duration: 8000,
      });
      onError?.(err instanceof Error ? err : new Error("Unknown error"));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <motion.form
      onSubmit={handleSubmit}
      noValidate
      className={cn(styles.form, className)}
      initial={prefersReducedMotion ? false : { opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={
        prefersReducedMotion
          ? { duration: 0 }
          : { duration: 0.5, ease: [0.16, 1, 0.3, 1] }
      }
    >
      {/* Honeypot */}
      <div className={styles.honeypot} aria-hidden="true">
        <label htmlFor={CONTACT_HONEYPOT_INPUT_ID} className="sr-only">
          {t("contactSpamTrapLabel")}
        </label>
        <input
          id={CONTACT_HONEYPOT_INPUT_ID}
          type="text"
          name={CONTACT_HONEYPOT_INPUT_NAME}
          tabIndex={-1}
          autoComplete="off"
          data-1p-ignore
          data-lpignore="true"
          value={formData.honeypot}
          onChange={updateField("honeypot")}
        />
      </div>

      {/* === TIER 1: Core Fields === */}
      <div className={styles.fields}>
        <FormFieldEditorial
          ref={nameRef}
          id="contact-name"
          label={t("contactFullName")}
          type="text"
          required
          value={formData.name}
          onChange={updateField("name")}
          onBlur={(event) => validateField("name", event.currentTarget.value)}
          error={formErrors.name}
          autoComplete="name"
          autoFocus
        />

        <div className={styles.fieldWithSuggestion}>
          <FormFieldEditorial
            ref={emailRef}
            id="contact-email"
            label={t("contactEmail")}
            type="email"
            required
            value={formData.email}
            onChange={updateField("email")}
            onBlur={(event) =>
              validateField("email", event.currentTarget.value)
            }
            error={formErrors.email}
            aria-describedby={
              emailSuggestion ? "contact-email-suggestion" : undefined
            }
            autoComplete="email"
          />
          {emailSuggestion && (
            <p
              id="contact-email-suggestion"
              className={styles.emailSuggestion}
              role="status"
            >
              {emailSuggestion}
            </p>
          )}
        </div>

        <FormFieldEditorial
          ref={messageRef}
          id="contact-message"
          label={t("contactMessage")}
          type="textarea"
          required
          value={formData.message}
          onChange={updateField("message")}
          onBlur={(event) =>
            validateField("message", event.currentTarget.value)
          }
          error={formErrors.message}
          placeholder={t("contactMessagePlaceholder")}
          rows={5}
        />
      </div>

      {/* === TIER 2: Project Details === */}
      <ExpandableSection
        collapsedLabel={t("contactAddProjectDetails", "Add project details")}
        expandedLabel={t("contactHideProjectDetails", "Hide project details")}
        expanded={tier2Expanded}
        onExpandedChange={setTier2Expanded}
        className={styles.expandable}
      >
        <div className={styles.fields}>
          <Combobox
            className={styles.alignedCombobox}
            label={t("contactPackageLabel", "Package")}
            placeholder={t("contactPackageNone", "Not sure yet / custom")}
            value={formData.engagementPackage}
            onValueChange={(value) =>
              dispatchForm({ type: "SET_PACKAGE", payload: value })
            }
            options={[
              {
                value: "",
                label: t("contactPackageNone", "Not sure yet / custom"),
              },
              ...ENGAGEMENTS.map((engagement) => ({
                value: engagement.packageId,
                label: packageSummary(engagement),
              })),
            ]}
          />

          {selectedEngagement ? (
            <FormFieldEditorial
              id="contact-package-budget"
              label={t("contactPackageBudgetLabel", "Budget")}
              type="text"
              readOnly
              value={`${t(selectedEngagement.priceKey)}, ${t(selectedEngagement.durationKey)}`}
              helperText={t("contactPackageBudgetHelper", {
                package: t(selectedEngagement.nameKey),
              })}
            />
          ) : (
            <Combobox
              className={styles.alignedCombobox}
              label={t("contactBudgetLabel", "Budget range")}
              placeholder={t("contactSelectPlaceholder", "Select...")}
              value={formData.budget}
              onValueChange={updateSelectField("budget")}
              options={[
                { value: "", label: t("contactSelectPlaceholder", "Select...") },
                ...BUDGET_OPTIONS.map((opt) => ({
                  value: opt.value,
                  label: t(opt.labelKey),
                })),
              ]}
            />
          )}

          <Combobox
            className={styles.alignedCombobox}
            label={t("contactTimelineLabel", "Timeline")}
            placeholder={t("contactSelectPlaceholder", "Select...")}
            value={formData.timeline}
            onValueChange={updateSelectField("timeline")}
            options={[
              { value: "", label: t("contactSelectPlaceholder", "Select...") },
              ...TIMELINE_OPTIONS.map((opt) => ({
                value: opt.value,
                label: t(opt.labelKey),
              })),
            ]}
          />

          <MultiCombobox
            className={styles.alignedCombobox}
            label={t("contactProjectTypeLabel", "Project type")}
            options={PROJECT_TYPE_OPTIONS.map((opt) => ({
              value: opt.value,
              label: t(opt.labelKey),
            }))}
            value={formData.projectType}
            onValueChange={(values) =>
              dispatchForm({ type: "UPDATE_PROJECT_TYPES", payload: values })
            }
            placeholder={t(
              "contactMultiSelectPlaceholder",
              "Select one or more...",
            )}
          />
        </div>

        {/* === TIER 3: Relationship & Brief === */}
        <ExpandableSection
          collapsedLabel={t("contactTellUsMore", "Add more details")}
          expandedLabel={t("contactHideTellUsMore", "Hide")}
          expanded={tier3Expanded}
          onExpandedChange={setTier3Expanded}
          className={styles.expandable}
        >
          <div className={styles.fields}>
            <PhoneInput
              label={t("contactPhone")}
              placeholder={t("contactPhonePlaceholder")}
              value={formData.phone}
              onChange={handlePhoneChange}
            />

            <CheckboxGroup
              id="contact-follow-up-preferences"
              label={t("contactFollowUpPreferencesLabel")}
              showMasterCheckbox={false}
              options={FOLLOW_UP_OPTIONS.map((opt) => ({
                value: opt.value,
                label: t(opt.labelKey),
              }))}
              defaultSelected={formData.followUpPreferences}
              onChange={(values) =>
                dispatchForm({
                  type: "UPDATE_FOLLOW_UP_PREFERENCES",
                  payload: values,
                })
              }
            />

            <Combobox
              className={styles.alignedCombobox}
              label={t("contactHearAbout")}
              placeholder={t("contactSelectPlaceholder", "Select...")}
              value={formData.hearAbout}
              onValueChange={updateSelectField("hearAbout")}
              options={[
                {
                  value: "",
                  label: t("contactSelectPlaceholder", "Select..."),
                },
                ...HEAR_ABOUT_OPTIONS.map((opt) => ({
                  value: opt.value,
                  label: t(opt.labelKey),
                })),
              ]}
            />

            <div className={styles.fileUploadWrapper}>
              <FileUpload
                appearance="editorial"
                label={t("contactAttachmentLabel")}
                placeholder={t("contactAttachmentPlaceholder")}
                helperText={t("contactAttachmentHelper", {
                  size: t("contactAttachmentSizeLabel", {
                    size: attachmentSizeValue,
                  }),
                  emailLimit: emailAttachmentLimitLabel,
                  types: t("contactAttachmentAcceptedTypes"),
                })}
                uploadButtonLabel={t("contactAttachmentUpload")}
                clearButtonLabel={t("contactAttachmentClear")}
                accept={ACCEPTED_ATTACHMENT_TYPES}
                maxSizeInBytes={MAX_ATTACHMENT_BYTES}
                sizeErrorMessage={t("contactAttachmentTooLarge", {
                  size: t("contactAttachmentSizeLabel", {
                    size: attachmentSizeValue,
                  }),
                })}
                value={attachmentFile}
                onFileChange={handleAttachmentChange}
                error={attachmentError}
              />
              {isAttachmentTooLargeForEmail && (
                <p className={styles.attachmentNotice}>
                  {attachmentEmailNotice}
                </p>
              )}
            </div>

            <FormFieldEditorial
              label={t("contactInspirationLabel", "Inspiration / References")}
              type="textarea"
              value={formData.inspiration}
              onChange={updateField("inspiration")}
              placeholder={t(
                "contactInspirationPlaceholder",
                "Share any links or references...",
              )}
              rows={2}
            />
          </div>
        </ExpandableSection>
      </ExpandableSection>

      {/* Privacy Policy */}
      <p className={styles.privacy}>
        {t("contactPrivacyPolicy1")}{" "}
        <a href="/privacy-policy" className={styles.privacyLink}>
          {t("contactPrivacyPolicy2")}
        </a>
        .
      </p>

      {/* Submit Button */}
      <Button
        submits
        variant="primary"
        size="lg"
        loading={isSubmitting}
        disabled={isSubmitting}
        className={styles.submitButton}
      >
        {isSubmitting
          ? t("contactSubmitting", "Sending...")
          : t("contactSubmit")}
      </Button>
    </motion.form>
  );
}

ContactFormEditorial.displayName = "ContactFormEditorial";
